import { Star, Heart, MapPin, BadgeCheck } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useFavorite } from "@/lib/favorites-store";
import { SPACES as spaces } from "@/lib/spaces-data";
import { Riyal } from "@/components/ui/riyal";

export function FeaturedSpaces() {
  const { t } = useTranslation();
  return (
    <section id="featured" className="bg-surface py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div className="max-w-xl">
            <div className="text-xs font-semibold uppercase tracking-widest text-primary">{t("featured.eyebrow")}</div>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">{t("featured.title")}</h2>
          </div>
        </div>
      </div>

      <div className="mt-12 flex gap-5 overflow-x-auto px-4 pb-6 sm:px-6 lg:px-8 hide-scrollbar">
        <div className="flex gap-5">
          {spaces.map((s, i) => (
            <FeaturedCard key={i} s={s} id={String(i + 1)} title={t(`featured.spaces.${i + 1}.title`, { defaultValue: s.title })} cityLabel={t(`hero.cityOpt.${s.city}`, { defaultValue: s.city })} />
          ))}
          <div className="w-1 shrink-0" aria-hidden />
        </div>
      </div>
    </section>
  );
}

function FeaturedCard({ s, id, title, cityLabel }: { s: (typeof spaces)[number]; id: string; title: string; cityLabel: string }) {
  const { t } = useTranslation();
  const { saved, toggle } = useFavorite(id);
  return (
    <article className="group w-[300px] shrink-0 overflow-hidden rounded-3xl border border-border bg-card shadow-soft transition-all hover:-translate-y-1 hover:shadow-elevated sm:w-[340px]">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={s.img}
          alt={title}
          width={900}
          height={700}
          loading="lazy"
                decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {s.tag && (
          <span className="absolute start-3 top-3 rounded-full bg-card/95 px-2.5 py-1 text-[11px] font-semibold text-foreground shadow-soft backdrop-blur">
            {t(`featured.tags.${s.tag}`, { defaultValue: s.tag })}
          </span>
        )}
        <button
          type="button"
          aria-label={saved ? t("featured.remove") : t("featured.save")}
          aria-pressed={saved}
          onClick={toggle}
          className="absolute end-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-card/90 text-foreground shadow-soft backdrop-blur transition-colors hover:text-primary"
        >
          <Heart className={cn("h-4 w-4 transition-colors", saved && "fill-primary text-primary")} />
        </button>
        {s.subscription && (
          <span
            title={t("featured.subscription")}
            className="absolute bottom-3 start-3 inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground shadow-soft"
          >
            <BadgeCheck className="h-3.5 w-3.5" /> {t("featured.subscription")}
          </span>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-base font-semibold leading-snug">{title}</h3>
          <div className="flex shrink-0 items-center gap-1 text-sm">
            <Star className="h-3.5 w-3.5 fill-foreground text-foreground" />
            <span className="font-medium">{s.rating}</span>
          </div>
        </div>
        <div className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3" /> {cityLabel} · {s.reviews} {t("featured.reviews")}
        </div>
        <div className="mt-4 flex items-end justify-between border-t border-border pt-4">
          <div>
            <span className="font-display text-xl font-bold"><Riyal /> {s.price}</span>
            <span className="text-sm text-muted-foreground"> {t("featured.perHour")}</span>
          </div>
          <Link
            to="/spaces/$spaceId"
            params={{ spaceId: id }}
            className="rounded-full bg-foreground px-4 py-1.5 text-xs font-medium text-background transition-opacity hover:opacity-90"
          >
            {t("featured.book")}
          </Link>
        </div>
      </div>
    </article>
  );
}
